import { NgClass } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormHelper } from '@common/form-helper';
import { Month } from '@common/month';
import { Months } from '@common/months';
import { CardComponent } from '@components/card/card.component';
import { InsuranceTypes } from '@enums/insurance-types';
import { Sites } from '@enums/sites';
import { environment } from '@environments/environment';
import { DateSelection } from '@models/date-selection';
import { PatientInfo } from '@models/patient-info';
import { RechargeRequest } from '@models/recharge-request';
import { TreatmentType } from '@models/treatment-types/treatment-type.model';
import { FormService } from '@services/form.service';
import { GenderService } from '@services/gender.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { NgxMaskDirective } from 'ngx-mask';

import { ageLimitValidator } from '../../validators/age-limit-validator';
import { dateValidator } from '../../validators/date-validator';

@Component({
  selector: 'app-patient-info',
  templateUrl: './patient-info.component.html',
  styleUrls: ['./patient-info.component.scss'],
  imports: [CardComponent, FormsModule, ReactiveFormsModule, NgClass, NgxMaskDirective],
})
export class PatientInfoComponent extends FormHelper implements OnInit {
  @Input() public enableGuardianForm: boolean;
  @Input() public patientInfoData: PatientInfo;

  public readonly InsuranceTypes: typeof InsuranceTypes = InsuranceTypes;

  public months: Month[];
  public days: number[];
  public years: number[];
  public minAgeAllowed: number;
  public consentDomain: string;

  public patientInfoForm: FormGroup;

  private storageService = inject(StorageService);
  private formService = inject(FormService);
  private orderService = inject(OrderService);
  private genderService = inject(GenderService);
  private treatmentTypeService = inject(TreatmentTypeService);

  /**
   * Get maximum age allowed
   */
  public get maxAgeAllowed(): number {
    return environment.maxAgeAllowed;
  }

  /**
   * Return true when recharge data is available. False otherwise.
   */
  public get isRechargePayment(): boolean {
    return !!this.orderService.rechargeData;
  }

  /**
   * Gets the error to display if sex pattern validation fails.
   */
  public get sexInvalidError(): string | null {
    const gender = this.patientInfoForm?.get('sex')?.value;

    return gender ? this.genderService.getGenderInvalidError(this.storageService.treatmentType, gender) : null;
  }

  /**
   * Gets the information message to display for the sex field.
   */
  public get genderInfoMessage(): string {
    return this.genderService.getGenderInfoMessage(
      this.storageService.treatmentType,
      this.patientInfoForm?.get('sex')?.value,
    );
  }

  /**
   * Gets the patient contact method.
   */
  public get patientContactVia(): 'email' | 'phone' | 'none' {
    return this.patientInfoForm.get('contactVia').value;
  }

  /**
   * Determines whether or not the guardian form should be shown.
   */
  public get showGuardianForm(): boolean {
    return this.enableGuardianForm && this.patientInfoForm.get('birthday').valid && this.isUnderAge();
  }

  /**
   * Determines whether the partner email input should be shown.
   */
  public get showPartnerEmailInput(): boolean {
    return this.patientInfoForm.get('hasPartner').value;
  }

  /**
   * Determines whether if it's STDCheck domain
   */
  public get isSTDCheck(): boolean {
    return (environment.site as Sites) === Sites.Stdcheck;
  }

  /**
   * Determines whether if it's HealthLabs domain
   */
  public get isHealthLabs(): boolean {
    return (environment.site as Sites) === Sites.Healthlabs;
  }

  /**
   * Determines whether if it's TreatMyUti domain
   */
  public get isTreatMyUti(): boolean {
    return (environment.site as Sites) === Sites.Treatmyuti;
  }

  /**
   * Determines whether if it's Starfish domain
   */
  public get isStarfish(): boolean {
    return (environment.site as Sites) === Sites.Starfish;
  }

  /**
   * Determines whether if it's Stallion domain
   */
  public get isStallion(): boolean {
    return (environment.site as Sites) === Sites.Stallion;
  }

  /**
   * Determines whether if it's Testosterone HQ domain
   */
  public get isTestosteroneHq(): boolean {
    return (environment.site as Sites) === Sites.TestosteroneHq;
  }

  /**
   * Determine if Partner Testing is allowed for the current domain.
   */
  public get isPartnerTestingAllowed(): boolean {
    return environment.order.isPartnerTestingAllowed;
  }

  /**
   * Return ' (or anybody else)' if it's HealthLabs domain
   */
  public get orAnyoneElseWithHl(): string {
    return this.isHealthLabs ? '(or anybody else) ' : '';
  }

  /**
   * Returns true when we want to display the phone field
   */
  public get showPhoneField(): boolean {
    return this.patientContactVia === 'phone' || environment.order.shouldShowPhoneFieldOnOrderRoute;
  }

  /**
   * Returns true when free order is set in localStorage
   */
  public get isFree(): boolean {
    return this.storageService.free;
  }

  /**
   * Returns true when isActive from baOrder is true in localStorage
   */
  public get isBaOrder(): boolean {
    return this.storageService.baOrder?.isActive;
  }

  /**
   * Gets the treatment type of the current order if available.
   */
  public get treatmentType(): TreatmentType | null {
    return this.treatmentTypeService.getTreatmentType(this.storageService.treatmentType);
  }

  /**
   * Whether the results should always be sent by email.
   */
  private get forceEmailSelection(): boolean {
    return environment.order.forceEmailResults;
  }

  /**
   * Gets the birthdate of the patient.
   */
  private get patientBirthDate(): DateSelection | null {
    if (!this.birthdateFilled()) {
      return null;
    }

    const birthDayFormGroup = this.patientInfoForm.get('birthday');

    return new DateSelection(
      Number(birthDayFormGroup.get('month').value),
      Number(birthDayFormGroup.get('day').value),
      Number(birthDayFormGroup.get('year').value),
    );
  }

  /**
   * Gets the birthdate validators based on the age restriction settings.
   */
  private get birthdayValidators(): ValidatorFn[] {
    return [dateValidator(), ageLimitValidator(this.minAgeAllowed, environment.maxAgeAllowed)];
  }

  /**
   * Initialize this component.
   */
  public ngOnInit(): void {
    this.consentDomain = environment.domain;
    this.setBirthdayFormOptions();
    this.patientInfoForm = this.formService.checkout.get('patient') as FormGroup;
    this.setDynamicPatientInfoFormValidators();

    if (this.patientInfoData) {
      this.patientInfoForm.patchValue(this.patientInfoData);
    } else if (this.isRechargePayment) {
      this.loggedInPatientForm(this.orderService.rechargeData);
    }

    this.storageService.hasPartner = false;
    this.autofillEmail();

    if (this.forceEmailSelection) {
      this.patientInfoForm.patchValue({ contactVia: 'email' });
      this.patientInfoForm.get('contactVia').disable();
    }
  }

  /**
   * Gets a list of all the days
   */
  public getDays(): number[] {
    return this.getListOfNumbers(1, 31);
  }

  /**
   * Gets a list of the last 90 years.
   *
   * The latest years are removed based minimum age settings.
   */
  public getYears(): number[] {
    const currentYear = new Date().getFullYear();
    const from = currentYear - this.minAgeAllowed;

    return this.getListOfNumbers(from, currentYear - environment.maxAgeAllowed);
  }

  /**
   * Determines if a form control is invalid.
   *
   * @param {string} controlName The name of the form control to check for validity.
   */
  public invalidControl(controlName: string): boolean {
    return this.isInvalid(this.patientInfoForm.get(controlName));
  }

  /**
   * Returns true if the birthday control is invalid and has been interacted with, false otherwise.
   */
  public invalidBirthdayControl(): boolean {
    const birthday = this.patientInfoForm.get('birthday');

    if (!birthday?.invalid) {
      return false;
    }

    const month = birthday.get('month');
    const day = birthday.get('day');
    const year = birthday.get('year');

    return (month.dirty || month.touched) && (day.dirty || day.touched) && (year.dirty || year.touched);
  }

  /**
   * Render reorder patient data
   *
   * @param {RechargeRequest} rechargeData Patient information from previous payment
   */
  public loggedInPatientForm(rechargeData: RechargeRequest): void {
    this.patientInfoForm.patchValue({
      contactVia: rechargeData.accountType,
      phone: rechargeData.phone,
      email: rechargeData.email,
    });
  }

  /**
   * Save the has partner attribute into the storage
   */
  public handleHasPartnerChange(): void {
    this.storageService.hasPartner = this.patientInfoForm.get('hasPartner').value;
  }

  /**
   * Sets the birthday select options.
   */
  private setBirthdayFormOptions(): void {
    this.minAgeAllowed = this.treatmentType?.minAgeAllowed || environment.order.minAgeAllowed;
    this.months = Months;
    this.days = this.getDays();
    this.years = this.getYears();
  }

  /**
   * Determines if the patient is underage.
   */
  private isUnderAge(): boolean {
    return !this.patientBirthDate?.isAgeValid();
  }

  /**
   * Sets up dynamic validators.
   */
  private setDynamicPatientInfoFormValidators(): void {
    const patientInfoForm = this.patientInfoForm;
    const sexControl = patientInfoForm.get('sex');
    const birthDayControl = patientInfoForm.get('birthday');
    const insuranceTypeControl = patientInfoForm.get('insuranceType');

    sexControl.setValidators([
      Validators.required,
      this.createGenderValidator(), // Use the new validator factory method
    ]);
    sexControl.updateValueAndValidity();

    birthDayControl.setValidators(this.birthdayValidators);
    birthDayControl.valueChanges.subscribe(() => this.setGuardianFormValidator());

    patientInfoForm.get('contactVia').valueChanges.subscribe(() => this.setContactFormValidator());
    patientInfoForm
      .get('hasPartner')
      .valueChanges.subscribe((hasPartner) => this.setPartnerEmailFormValidator(hasPartner));

    if (this.treatmentType?.requiresInsuranceTypeSelection) {
      insuranceTypeControl?.setValidators([Validators.required, this.createInsuranceTypeValidator()]);
    } else {
      insuranceTypeControl?.clearValidators();
    }

    insuranceTypeControl?.updateValueAndValidity();

    this.setGuardianFormValidator();
    this.setContactFormValidator();
  }

  /**
   * Creates a validator function that checks gender validity using GenderService.
   */
  private createGenderValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const gender = control.value;
      if (!gender) {
        return null;
      }

      const error = this.genderService.getGenderInvalidError(this.storageService.treatmentType, gender);

      return error !== null ? { invalidGender: true } : null;
    };
  }

  /**
   * Creates a validator function that checks insurance type validity.
   *
   * @returns {ValidatorFn} the validator fn to validate accepted insurance types
   */
  private createInsuranceTypeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const insuranceType = control.value;

      if (!insuranceType) {
        return null;
      }

      return insuranceType === InsuranceTypes.PrivateInsurance || insuranceType === InsuranceTypes.Medicaid
        ? null
        : { invalidInsuranceType: true };
    };
  }

  /**
   * Sets the contact form validator based on the contact type.
   */
  private setContactFormValidator(): void {
    const patientInfoForm = this.patientInfoForm;
    const emailControl = patientInfoForm.get('email');
    const phoneControl = patientInfoForm.get('phone');
    const allowVmControl = patientInfoForm.get('voicemail_allowed');

    emailControl.clearValidators();
    phoneControl.clearValidators();
    allowVmControl.clearValidators();

    switch (patientInfoForm.get('contactVia').value) {
      case 'email':
        emailControl.setValidators([Validators.required, Validators.email]);
        break;
      case 'phone':
        phoneControl.setValidators([Validators.required, Validators.pattern('[0-9]{10}')]);
        allowVmControl.setValidators([Validators.required]);
        break;
    }
    if (environment.order.shouldShowPhoneFieldOnOrderRoute) {
      phoneControl.setValidators([Validators.required, Validators.pattern('[0-9]{10}')]);
    }

    emailControl.updateValueAndValidity();
    phoneControl.updateValueAndValidity();
    allowVmControl.updateValueAndValidity();
  }

  /**
   * Sets the partner email form validator based on the has partner checkbox
   */
  private setPartnerEmailFormValidator(hasPartner: boolean): void {
    const partnerEmailField = this.patientInfoForm.get('partnerEmail');

    if (hasPartner) {
      partnerEmailField.addValidators(Validators.required);

      return;
    }

    partnerEmailField.removeValidators(Validators.required);
    partnerEmailField.updateValueAndValidity();
  }

  /**
   * Sets the guardian form validators based on the age of the patient.
   */
  private setGuardianFormValidator(): void {
    if (!this.enableGuardianForm) {
      return;
    }

    const guardianForm = this.patientInfoForm.get('parentGuardian');
    ['firstName', 'lastName', 'relationToChild', 'attestation'].forEach((controlName: string) => {
      const control = guardianForm.get(controlName);
      control.clearValidators();
      if (this.isUnderAge()) {
        control.setValidators(
          [Validators.required].concat(controlName === 'attestation' ? [Validators.requiredTrue] : []),
        );
      }

      control.updateValueAndValidity();
    });
    guardianForm.updateValueAndValidity();
  }

  /**
   * Autofill e-mail when set in localStorage
   */
  private autofillEmail(): void {
    if (this.storageService.email) {
      this.patientInfoForm.get('contactVia').setValue('email');
      this.patientInfoForm.get('email').setValue(this.storageService.email);
    }
  }

  /**
   * Determines if the birthdate is filled out.
   */
  private birthdateFilled(): boolean {
    return ['month', 'day', 'year'].every((controlName: string) => {
      return !this.invalidControl(`birthday.${controlName}`);
    });
  }
}
